import { useState, useEffect, useCallback } from "react";
import { fetchDevices, type Device, ApiError } from "@/lib/api";
import { DeviceCard } from "@/components/DeviceCard";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; devices: Device[] };

export function Devices() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [roleFilter, setRoleFilter] = useState<"all" | "server" | "player">(
    "all",
  );

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await fetchDevices();
      setState({ status: "loaded", devices: data.devices });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Server error (${err.status})`
          : "Failed to load devices. Check your connection and try again.";
      setState({ status: "error", message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-4">
          <div className="skeleton h-8 w-40 rounded-lg" />
          <div className="skeleton h-4 w-64 rounded" />
          <div className="space-y-3 mt-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-28 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-zinc-100 mb-2">
            Could not load devices
          </h2>
          <p className="text-sm text-zinc-400 mb-6">{state.message}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { devices } = state;

  const filtered =
    roleFilter === "all"
      ? devices
      : devices.filter((d) => d.role === roleFilter);

  const onlineCount = devices.filter((d) => d.status === "online").length;
  const serverCount = devices.filter((d) => d.role === "server").length;
  const playerCount = devices.filter((d) => d.role === "player").length;
  const updatesAvailable = devices.filter((d) => d.updateAvailable).length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
          Devices
        </h1>
        <p className="text-zinc-400">
          Manage and monitor your Blazeboard installations.
        </p>
      </div>

      {devices.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-xs text-zinc-500 mb-0.5">Total</p>
            <p className="text-xl font-semibold text-zinc-100">
              {devices.length}
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-xs text-zinc-500 mb-0.5">Online</p>
            <p className="text-xl font-semibold text-green-400">
              {onlineCount}
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-xs text-zinc-500 mb-0.5">Offline</p>
            <p className="text-xl font-semibold text-zinc-500">
              {devices.length - onlineCount}
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-xs text-zinc-500 mb-0.5">Updates</p>
            <p
              className={`text-xl font-semibold ${updatesAvailable > 0 ? "text-blue-400" : "text-zinc-500"}`}
            >
              {updatesAvailable}
            </p>
          </div>
        </div>
      )}

      {devices.length > 0 && (
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => setRoleFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              roleFilter === "all"
                ? "bg-zinc-800 text-zinc-100"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            All ({devices.length})
          </button>
          <button
            onClick={() => setRoleFilter("server")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              roleFilter === "server"
                ? "bg-zinc-800 text-zinc-100"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            Servers ({serverCount})
          </button>
          <button
            onClick={() => setRoleFilter("player")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              roleFilter === "player"
                ? "bg-zinc-800 text-zinc-100"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            Players ({playerCount})
          </button>
        </div>
      )}

      {devices.length === 0 ? (
        <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-zinc-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z"
              />
            </svg>
          </div>
          <h3 className="font-medium text-zinc-300 mb-1">
            No devices registered
          </h3>
          <p className="text-sm text-zinc-500 max-w-md mx-auto mb-6">
            You haven't set up any Blazeboard devices yet. Head to the setup
            page to get your first device running.
          </p>
          <a
            href="/setup"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            Go to setup
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-xl p-8 text-center">
          <h3 className="font-medium text-zinc-300 mb-1">
            No {roleFilter} devices
          </h3>
          <p className="text-sm text-zinc-500">
            You don't have any {roleFilter} devices registered. Try a different
            filter.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((device) => (
            <DeviceCard key={device.id} device={device} />
          ))}
        </div>
      )}

      {devices.length > 0 && (
        <div className="mt-8 flex justify-end">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
